<template>
  <div class="my-10 flex flex-col gap-5">
    <ReportModal :item-id="currentId" origin="businessAccount"></ReportModal>

    <Modal :show-modal="reportModal" @cancel="closeReportModal">
      <template v-slot:header>
        <p class="text-3xl font-bold text-main-color">{{ $t('page.report') }}</p>
      </template>
      <template v-slot:content>
        <div class="flex flex-col">
          <Form @submit="postBusinessReport" :validation-schema="reportPublicationSchema()">
            <div class="w-full flex flex-col gap-2">
              <label for="report_type" class="text-start font-semibold text-main-color">{{
                $t('form.reportType')
              }}</label>
              <Field
                v-model="formReport.report_type"
                as="select"
                name="report_type"
                id="report_type"
                class="w-full rounded flex border justify-between px-8 py-5 placeholder-[#5F5F5F] max-md:py-4"
              >
                <option selected disabled>{{ $t('page.selectValue') }}</option>
                <template v-if="$store.reportTypes.length > 0">
                  <option v-for="report in $store.reportTypes" :value="report.id" :key="report.id">
                    {{ report.name }}
                  </option>
                </template>
              </Field>
              <ErrorMessage name="report_type" class="text-red-500"></ErrorMessage>
            </div>
            <div class="mt-5 flex flex-col gap-2">
              <label for="content" class="text-start font-semibold text-main-color">{{
                $t('form.message')
              }}</label>
              <Field
                v-model="formReport.content"
                as="textarea"
                name="content"
                id="content"
                cols="30"
                rows="4"
                class="w-full rounded border px-8 py-5 placeholder-[#5F5F5F] resize-none max-md:py-4"
              >
              </Field>
              <ErrorMessage name="content" class="text-red-500"></ErrorMessage>
            </div>
            <div class="mt-5 flex gap-5">
              <SubmitBtn type="submit" :loading="loading">{{ $t('action.send') }}</SubmitBtn>
              <button class="w-full text-main-color font-bold">{{ $t('action.cancel') }}</button>
            </div>
          </Form>
        </div>
      </template>
    </Modal>
    <div class="">
      <div v-if="bannersLoaded">
        <Splide
          @splide:move="stopVideo"
          ref="splide"
          class="w-full"
          :options="{
            slideFocus: true,
            type: 'loop',
            autoplay: false,
            pagination: false,
            pauseOnHover: false,
          }"
          :has-track="false"
          aria-label="similars"
        >
          <SplideTrack class="py-4">
            <SplideSlide v-for="el in detail?.banners" :key="keyvideo[`video${el.id}`]">
              <video
                controlsList="nodownload"
                v-if="isVideo(el.path)"
                :ref="'video' + el.id"
                controls
                :src="el?.path"
                class="object-contain h-[450px] w-full"
              ></video>
              <img
                v-else
                class="rounded-t h-[450px] w-full object-fill"
                :src="el?.path"
                alt="store-banner"
              />
            </SplideSlide>
          </SplideTrack>
        </Splide>
      </div>
      <div class="w-full flex flex-col gap-5 bg-white p-3 rounded-b">
        <div class="flex items-center gap-3 min-w-0">
          <h1
            class="font-bold text-2xl text-main-color flex-1 min-w-0 truncate"
            :title="detail?.name"
          >
            {{ detail?.name }}
          </h1>
          <div class="flex items-center gap-2 shrink-0">
            <IconEye></IconEye>
            <span class="font-bold text-main-color text-sm">{{ detail?.view_count }}</span>
          </div>
          <VueStarRating
            :rounded-corners="true"
            :star-size="15"
            active-color="#ffb300"
            inactive-color="#f2f2f2"
            :border-width="3"
            border-color="#ffb300"
            :show-rating="false"
            :read-only="true"
            :increment="0.5"
            :rating="detail?.rating"
          ></VueStarRating>
          <div class="flex items-center gap-1 min-w-0" v-if="detail?.work_hours">
            <IconClock class="shrink-0" />
            <span class="min-w-0 truncate" :title="detail?.work_hours[getDay.toLowerCase()]">
              {{ detail?.work_hours[getDay.toLowerCase()] }}
            </span>
          </div>
          <button
            :disabled="loading"
            @click="likeAction"
            class="rounded-full bg-[#409DBA] h-[2.1875rem] w-[2.1875rem] flex justify-center items-center cursor-pointer shrink-0"
          >
            <IconHeart :class="{ 'fill-white': isLiked }" />
          </button>
          <div class="flex items-center gap-5 ml-auto shrink-0">
            <div
              v-if="detail?.is_vip"
              class="px-8 py-2.5 bg-[#7951F9] rounded-lg text-white font-bold text-end max-md:py-2 max-md:px-4"
            >
              VIP
            </div>

            <div
              v-if="detail?.subscription !== ''"
              class="w-max rounded-lg py-2.5 px-8 font-bold max-md:py-2 max-md:px-4"
              :class="getSubcriptionType?.color"
            >
              {{ getSubcriptionType?.name }}
            </div>
          </div>
        </div>
        <div class="flex items-center gap-3">
          <ReportButton
            class="bg-[#797979] text-base !rounded-lg !py-2.5 !px-8 w-max text-white font-semibold"
          ></ReportButton>

          <a
            :href="`tel:${detail?.phone}`"
            class="py-2.5 px-8 block text-center rounded-lg bg-main-color text-base text-white font-semibold"
            >{{ $t('action.call') }}</a
          >
        </div>
      </div>
    </div>
    <div
      class="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-16 bg-white p-8 rounded-b max-md:grid-cols-1 max-md:gap-5"
    >
      <div class="w-full min-w-0">
        <div class="flex justify-between items-center text-main-color mb-3 gap-3 min-w-0">
          <p class="text-3xl font-bold flex-1 min-w-0 truncate" :title="detail?.name">
            {{ detail?.name }}
          </p>
          <div class="flex items-center gap-2 shrink-0">
            <IconEye></IconEye>
            <span class="font-bold text-sm">{{ detail?.view_count }}</span>
          </div>
        </div>
        <div class="text-[#333333] text-base pb-4 border-b border-[#E3E3E3]">
          <p class="font-light">{{ detail?.business_type }}</p>
        </div>
        <div class="mt-3">
          <p class="tetx-lg text-main-color mb-3">
            {{ $t('page.category') }}: <span class="font-semibold">{{ detail?.category }}</span>
          </p>
          <p class="tetx-lg text-main-color mb-3">
            {{ $t('page.jobType') }}: <span class="font-semibold">{{ detail?.business_type }}</span>
          </p>
          <p class="tetx-lg text-main-color mb-3">
            {{ $t('page.location') }}: <span class="font-semibold">{{ detail?.location }}</span>
          </p>
          <p class="tetx-lg text-main-color mb-3">
            {{ $t('page.address') }}: <span class="font-semibold">{{ detail?.address }}</span>
          </p>
          <p class="tetx-lg text-main-color mb-3">
            {{ $t('form.phone') }}: <span class="font-semibold">{{ detail?.phone }}</span>
          </p>
          <p class="tetx-lg text-main-color mb-3">
            {{ $t('page.accountDate') }}:
            <span class="font-semibold">{{ detail?.date_started }}</span>
          </p>
          <p class="tetx-lg text-main-color mb-3">
            {{ $t('page.website') }}:
            <span class="font-semibold"
              ><a class="!text-accent-color" :href="detail?.link">{{ detail?.link }}</a>
            </span>
          </p>
        </div>
      </div>
      <div class="w-full min-w-0">
        <div class="accordion">
          <Accordion :label="$t('page.detail')">
            <p>{{ detail?.description }}</p>
          </Accordion>
          <Accordion v-if="detail?.work_hours" :label="$t('page.workingHours')">
            <div class="flex flex-col gap-2">
              <p>
                <span class="font-semibold">{{ $t('page.monday') }}: </span
                >{{ detail?.work_hours[`monday`] }}
              </p>
              <p>
                <span class="font-semibold">{{ $t('page.tuesday') }}: </span
                >{{ detail?.work_hours[`tuesday`] }}
              </p>
              <p>
                <span class="font-semibold">{{ $t('page.wednesday') }}: </span
                >{{ detail?.work_hours[`wednesday`] }}
              </p>
              <p>
                <span class="font-semibold">{{ $t('page.thursday') }}: </span
                >{{ detail?.work_hours[`thursday`] }}
              </p>
              <p>
                <span class="font-semibold">{{ $t('page.friday') }}: </span
                >{{ detail?.work_hours[`friday`] }}
              </p>
              <p>
                <span class="font-semibold">{{ $t('page.saturday') }}: </span
                >{{ detail?.work_hours[`saturday`] }}
              </p>
              <p>
                <span class="font-semibold">{{ $t('page.sunday') }}: </span
                >{{ detail?.work_hours[`sunday`] }}
              </p>
            </div>
          </Accordion>
          <Accordion :label="$t('page.phoneNumbers')">
            <div class="flex flex-col gap-2">
              <a
                v-for="(el, index) in detail?.phone_numbers"
                :key="index"
                :href="`tel:${el.phone}`"
                >{{ el.phone }}</a
              >
            </div>
          </Accordion>
          <Accordion v-if="detail?.contacts" :label="$t('page.contactsSocialMedia')">
            <div class="flex gap-2 flex-col">
              <a v-for="(el, index) in detail?.contacts" :key="index" :href="el.value"
                >{{ CONTACT_TYPE_OPTIONS[el.type] }}: {{ el.value }}</a
              >
            </div>
          </Accordion>
        </div>
      </div>
    </div>
    <PosterBannerSmall
      v-if="publicationSmallPosters.length > 0"
      :content="publicationSmallPosters"
    />
    <div v-if="detail?.categories?.length > 0" class="flex flex-wrap gap-3">
      <p class="text-xl font-bold">{{ $t('page.categories') }}</p>

      <Splide
        class="w-full"
        :options="SPLIDE_PILL_OPTIONS"
        :has-track="false"
        aria-label="similars"
      >
        <SplideTrack class="pb-4">
          <SplideSlide>
            <router-link
              :to="{ name: 'businessAccountsDetail', params: { id: currentId } }"
              class="w-max flex px-4 cursor-pointer items-center gap-1 justify-center p-3 text-lg bg-white rounded-md border border-solid border-gp-grey-70"
              :class="{ '!bg-main-color text-white': currentCategory == null }"
            >
              {{ $t('page.all') }}</router-link
            >
          </SplideSlide>
          <SplideSlide v-for="(el, idx) in detail.categories" :key="idx">
            <router-link
              :to="{
                name: 'businessAccountsDetail',
                params: { id: currentId },
                query: { category: el.id },
              }"
              class="w-max flex px-4 cursor-pointer items-center gap-1 justify-center p-3 text-lg bg-white rounded-md border border-solid border-gp-grey-70"
              :class="{ '!bg-main-color text-white': currentCategory == el.id }"
            >
              {{ el.name }}</router-link
            >
          </SplideSlide>
        </SplideTrack>
      </Splide>
    </div>
    <div class="grid grid-cols-4 gap-5 max-lg:grid-cols-3 max-md:grid-cols-2 max-sm:grid-cols-1">
      <router-link
        v-for="item in publications"
        :key="item.id"
        :to="{
          name: 'publicationDetail',
          params: { id: item.id },
          query: { category: this.currentCategory },
        }"
      >
        <PublicationCard :detail="item" :has_name="categoryDetail?.has_name"> </PublicationCard>
      </router-link>
    </div>
  </div>
</template>

<script>
import api from '@/api';
import { i18n } from '@/i18n';
import Modal from '@cmp/Modal.vue';
import Accordion from '@cmp/Accordion.vue';
import PosterBannerSmall from '@cmp/PosterBannerSmall.vue';

import BusinessAccountBanner from '@cmp/media/BusinessAccountBanner.vue';
import { Field, Form, ErrorMessage } from 'vee-validate';
import IconEye from '@icons/IconEye.vue';
import IconHeart from '@icons/IconHeart.vue';
import IconClock from '@icons/IconClock.vue';
import { SPLIDE_PILL_OPTIONS, CONTACT_TYPE_OPTIONS } from '@/constants';

import { reportPublicationSchema } from '@/schemas';
import VueStarRating from 'vue-star-rating';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide';
import { isVideo } from '@/helpers';
import PublicationCard from '@cmp/publication/PublicationCard.vue';
import ReportModal from '@cmp/modals/ReportModal.vue';
import ReportButton from '@cmp/modals/buttons/ReportButton.vue';
export default {
  name: 'businessAccountDetail',
  components: {
    Modal,
    Accordion,
    Field,
    Form,
    ErrorMessage,
    IconEye,
    IconHeart,
    BusinessAccountBanner,
    PosterBannerSmall,
    VueStarRating,
    Splide,
    SplideSlide,
    SplideTrack,
    IconClock,
    PublicationCard,
    ReportModal,
    ReportButton,
  },
  data() {
    return {
      loading: false,
      detail: null,
      reportModal: false,
      rateModal: false,
      publicationSmallPosters: [],
      userRating: 0,
      isLiked: false,
      publications: [],
      formReport: {
        content: '',
        report_type: '',
      },
      formRate: {
        rating: 0,
      },
      reportPublicationSchema: reportPublicationSchema,
      keyvideo: {},
      bannersLoaded: false,
      SPLIDE_PILL_OPTIONS: SPLIDE_PILL_OPTIONS,
      CONTACT_TYPE_OPTIONS: CONTACT_TYPE_OPTIONS,
      currentCategory: null,
    };
  },
  watch: {
    '$route.query.category': {
      //   immediate: true,
      async handler(newValue, oldValue) {
        if (newValue != oldValue) {
          this.currentCategory = newValue;
          await this.getPublications();
        }
      },
    },
  },
  methods: {
    isVideo,
    openReportModal() {
      if (this.$store.authStatus === false) {
        this.$store.setState('loginModal', true);
        return;
      }
      this.reportModal = true;
    },
    closeReportModal() {
      this.reportModal = false;
      this.formReport = {
        content: '',
        report_type: '',
      };
    },
    openRateModal() {
      if (this.$store.authStatus === false) {
        this.$store.setState('loginModal', true);
        return;
      }
      this.rateModal = true;
    },
    closeRateModal() {
      this.rateModal = false;
      this.formRate = {
        rating: 0,
      };
    },
    async getPublications() {
      this.loading = true;
      try {
        const response = await api.get('publications/publications/', {
          params: {
            business_account: this.currentId,
            category: this.currentCategory || null,
          },
        });
        this.publications = response.data.results;
      } catch (e) {
        console.log(e);
      }
      this.loading = false;
    },

    async getBusinessDetail() {
      this.loading = true;
      try {
        const response = await api.get(`posts/business_accounts/${this.currentId}/`);
        this.detail = response.data;
        this.isLiked = this.detail.is_liked;
        this.detail?.banners.forEach(element => {
          if (this.isVideo(element.path)) {
            let v = 'video' + element.id;
            this.keyvideo[v] = 1;
            console.log('keyvidoe');
          }
        });
        this.bannersLoaded = true;
      } catch (e) {
        console.log(e);
      }
      this.loading = false;
    },
    async postBusinessReport() {
      try {
        const response = await api.post(
          `posts/business_accounts/${this.currentId}/report/`,
          this.formReport,
        );
        // this.comments = response.data.results
        this.$toast.info(`${i18n.global.t('warning.PUBLICATION_REPORTED')}`);
        this.closeReportModal();
      } catch (e) {
        console.log(e);
      }
    },
    async postAccountRate() {
      try {
        const response = await api.post(
          `posts/business_accounts/${this.currentId}/rate/`,
          this.formRate,
        );
        // this.$toast.info(`${i18n.global.t('warning.PUBLICATION_REPORTED')}`)
        this.closeRateModal();
        this.userRating = response.data.rating;
      } catch (e) {
        console.log(e);
      }
    },
    async getPublicationSmallPoster() {
      try {
        const response = await api.get('commercials/advertisements/?type=215');
        this.publicationSmallPosters = response.data;
      } catch (e) {
        console.log(e);
      }
    },
    async likeAction() {
      if (this.$store.authStatus === false) {
        this.$store.setState('loginModal', true);
        return;
      }
      try {
        this.loading = true;
        const response = await api.post(`posts/business_accounts/${this.currentId}/like/`, {
          is_liked: !this.isLiked,
        });
        this.isLiked = !this.isLiked;
        this.$store.getLiked('business');
      } catch (e) {
        console.log(e);
      }
      this.loading = false;
    },
    stopVideo() {
      this.detail?.banners.forEach(element => {
        if (this.isVideo(element.path)) {
          let v = 'video' + element.id;
          this.keyvideo[v] += 1;
        }
      });
    },
    setUserRate(value) {
      this.userRating = value;
    },
  },
  computed: {
    getSubcriptionType() {
      switch (this.detail?.subscription) {
        case 'Gold':
          return { color: 'bg-[#FABB18] text-black', name: 'Gold' };
        case 'Silver':
          return { color: 'bg-[#C0C0C0] text-black', name: 'Silver' };
        case 'Bronze':
          return { color: 'bg-[#AE7145] text-white', name: 'Bronze' };
        default:
          break;
      }
    },
    getDay() {
      if (this.detail?.work_hours) {
        let date = new Date();
        return date.toLocaleString('en-us', { weekday: 'long' });
      }
      return null;
    },
  },
  created() {
    this.currentId = this.$route.params.id;
    this.currentCategory = this.$route.query.category || null;
    this.getPublications();
    this.getBusinessDetail();
    this.getPublicationSmallPoster();
  },
};
</script>
